import { Component, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import {
	GeneralAction,
	IModalAction,
	NavBreadcrumb,
	PageRequest
} from '@models/models';
import { ContactForm, Template } from '@web-components/settings/settings.model';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { SNModalService } from '@web-services/modal/snModal.service';
import { DeleteItemModalComponent } from '@web-components/settings/common/delete-item-modal/delete-item-modal';
import { SettingsDataService } from '@web-services/settings/contact-form/settings.data.service';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';
import { AccountServiceWebProvider } from '@web-services/user/account.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { NewSubscriptionServiceDataProvider } from '@services/subscription/subscription.service';
import {
	CommunicationEvents,
	SubscriptionStatusResponse
} from '@models/subscription.model';

@Component({
	selector: 'contact-form-section',
	template: require('./contact-form-section.html'),
	styles: [require('./contact-form-section.scss')]
})
export class ContactFormSectionComponent implements OnInit, OnDestroy {
	breadcrumbs: Array<NavBreadcrumb> = [
		{ link: '/', title: 'Dashboard' },
		{ link: '/settings', title: 'Settings' },
		{ link: null, title: 'Contact Form' }
	];
	contactForm: ContactForm;
	btnDisable: boolean;
	pageRequest: PageRequest;
	companyId: number;

	private subscriptionPaidEvent;
	private deleteModalRef: NgbModalRef;
	private unsubscribe: Subject<void> = new Subject();

	private readonly REQUEST_PARAMS: PageRequest = {
		paging: {
			pageNumber: 0,
			pageSize: 5,
			searchToken: null
		},
		sortCriterias: [
			{
				fieldName: 'name',
				sortAscending: true
			}
		]
	};

	constructor(
		private snModalService: SNModalService,
		private settingsDataService: SettingsDataService,
		private accountService: AccountServiceWebProvider,
		private subscriptionService: NewSubscriptionServiceDataProvider,
		private messageService: MessageService,
		private renderer: Renderer2
	) {}

	ngOnInit(): void {
		this.getCompanyId();
		this.applyPageProductFromLocalStorage();
		this.getContactFormPreviews();
		this.onSubscriptionPaid();
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
		if (this.subscriptionPaidEvent) {
			this.subscriptionPaidEvent();
		}
	}

	getCompanyId(): void {
		this.accountService.companyIdObservable.subscribe((data: number): void => {
			this.companyId = data;
		});
	}

	addNewTemplate(): void {
		if (this.contactForm.actions.UPGRADE_PLAN_FOR_CREATE_NEW_FORM) {
			this.subscriptionService.setChoosePlanOpenModalStatus({
				state: true,
				payload: {
					url: this.contactForm.actions.UPGRADE_PLAN_FOR_CREATE_NEW_FORM.url
				}
			});
		} else {
			window.open(
				`${this.contactForm.actions.OPEN_EDITOR_FOR_CREATION.url}`,
				'_self'
			);
		}
	}

	editTemplate(template: Template): void {
		window.open(`${template.formActions.OPEN_IN_EDITOR.url}`, '_self');
	}

	duplicateTemplate(template: Template): void {
		this.btnDisable = true;
		this.settingsDataService
			.duplicateContactForm(
				template.formActions.DUPLICATE_FORM.url,
				`${template.name} copy`
			)
			.pipe(
				takeUntil(this.unsubscribe),
				finalize((): boolean => (this.btnDisable = false))
			)
			.subscribe(
				(data: Template): void => {
					this.editTemplate(data);
				},
				({ error }): void => this.errorDuplicateForm(error)
			);
	}

	deleteTemplate(template: Template): void {
		this.deleteModalRef = this.snModalService.openModal(
			DeleteItemModalComponent
		);

		this.deleteModalRef.componentInstance.btnDisabled = false;
		this.deleteModalRef.componentInstance.itemName = template.name;
		this.deleteModalRef.componentInstance.modalTitle = 'Contact Form';
		this.deleteModalRef.componentInstance.modalData = template;

		const modalData$ = this.snModalService
			.getModalData()
			.subscribe((data: IModalAction<Template>): void => {
				this.settingsDataService
					.deleteContactForm(data.props.formActions.DELETE_FORM.url)
					.pipe(
						takeUntil(this.unsubscribe),
						finalize(
							(): boolean =>
								(this.deleteModalRef.componentInstance.btnDisabled = false)
						)
					)
					.subscribe((): void => {
						this.pageRequest.paging.pageNumber = 0;
						this.pageRequest.paging.searchToken = null;
						this.getContactFormPreviews();
						this.deleteModalRef.close();
						modalData$.unsubscribe();
					});
			});
	}

	updatePageRequestParams(data: PageRequest): void {
		this.pageRequest = data;
		this.getContactFormPreviews();
	}

	private getContactFormPreviews(): void {
		this.settingsDataService
			.getContactFormPreviews(this.pageRequest)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((data: ContactForm): void => {
				this.contactForm = data;
				this.pageRequest.paging.searchToken = data.formPreviews.searchToken;
				this.setPageProductToLocalStorage(this.pageRequest);
			});
	}

	private applyPageProductFromLocalStorage(): void {
		const pageRequestObject = JSON.parse(
			localStorage.getItem(`contactFormRequest_${this.companyId}`)
		);

		this.pageRequest = this.REQUEST_PARAMS;

		if (pageRequestObject && pageRequestObject !== '') {
			const { pageSize, fieldName, sortAscending } = pageRequestObject;
			this.pageRequest.paging.pageSize = pageSize;
			this.pageRequest.sortCriterias[0].fieldName = fieldName;
			this.pageRequest.sortCriterias[0].sortAscending = sortAscending;
		}
	}

	private setPageProductToLocalStorage(pageRequest: PageRequest): void {
		const pageRequestObject = {
			pageSize: pageRequest.paging.pageSize,
			fieldName: pageRequest.sortCriterias[0].fieldName,
			sortAscending: pageRequest.sortCriterias[0].sortAscending
		};

		const pageRequestString = JSON.stringify(pageRequestObject);

		localStorage.removeItem(`contactFormRequest_${this.companyId}`);
		localStorage.setItem(
			`contactFormRequest_${this.companyId}`,
			pageRequestString
		);
	}

	private errorDuplicateForm(error): void {
		const { errorTitle, errorMessage } = error;
		this.messageService.showErrorFromBackend(errorTitle, errorMessage);
	}

	private onSubscriptionPaid(): void {
		this.subscriptionPaidEvent = this.renderer.listen(
			'document',
			CommunicationEvents.planCheckoutClose,
			(data: CustomEvent): void =>
				this.successPaymentProcessed(data.detail.data)
		);
	}

	private successPaymentProcessed(data: SubscriptionStatusResponse): void {
		const needToConfirmPayment: GeneralAction =
			data.data.actions.COMPLETE_PAYMENT;
		if (!needToConfirmPayment) {
			this.getContactFormPreviews();
		}
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./web/modern/components/settings/contact-form-section/contact-form-section.ts